import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `sip-trunk` of the console gateway — the account's own SIP trunks (plan-008 ST-UN, design
 * §22.13.2 IC-77, §22.14). All 21 operations ST-AT documented, decoded in full.
 *
 * ── THE PASSWORDS ─────────────────────────────────────────────────────────────────────────
 * The outbound password travels in exactly two requests (`createSipTrunk`, `replaceSipTrunkPassword`)
 * and in no answer: `SipTrunk.password_set` is always `true` and {@link SipTrunkView} carries no
 * password field at all, so there is nothing for a screen to pre-fill or echo. The inbound password
 * appears in one answer only, `createSipTrunkInboundAuth`'s — {@link SipTrunkInboundAuthView} is
 * returned to the caller and never cached by this slice; the screen holds it in component state
 * until its dialog closes (the API key pattern of `routes/settings/api-key-panel.tsx`).
 *
 * ── AN INBOUND-ONLY TRUNK (plan-015 SIP, gap G13, ADR 0046) ─────────────────────────────────
 * A trunk created without the provider's outbound host, port, username, password or destination countries only
 * receives calls. The API answers its outbound half as `null`; this view keeps the two-way trunk's field types,
 * which Numbers & SIP's card, copy and edit form read as they are — `host` and `authUsername` EMPTY, `port` 0 (not a
 * port), no destination countries — and marks it `inboundOnly: true`. {@link isInboundOnlyTrunkView} is the one
 * test; nothing in the console dials or checks through a trunk, so those values are never used as an address.
 * Its first answered call verifies it: the API's `verified_by_first_call` event reads here as the `state_changed`
 * it is, with `verifiedByFirstCall: true` (Numbers & SIP's activity words have no row of their own for it yet).
 * `createSipTrunk` takes either shape; `updateSipTrunk` may give an inbound-only trunk its whole outbound half,
 * `password` included.
 *
 * ── A REFUSAL KEEPS ITS REQUEST ID ───────────────────────────────────────────────────────
 * A refused call is the generated client's `ApiError` (with `code`, `field` and `requestId`); a 2xx
 * body that does not decode is a {@link SipTrunkReadError} carrying the same id.
 *
 * ── THE COOLDOWN ─────────────────────────────────────────────────────────────────────────
 * `checkSipTrunk`'s `429 sip_trunk_check_cooldown` carries the real wait only in `Retry-After`, which
 * the generated client does not surface (`ApiResult` has no headers), and the envelope's message is
 * "never parsed". So the screen shows the API's own sentence — which states the wait — and never a
 * countdown of its own invention.
 *
 * ── THE TEST CALL'S PER-MINUTE LIMIT AND THE TRUNK MAXIMUM (plan-008 ST-RV2) ──────────────────
 * "The agent calls me" shares `startOutboundConversation`'s per-minute limits (review C2): its
 * `429 rate_limited` is raised as a `RetryAfterApiError` carrying the real `Retry-After`, which
 * `apiGateway`'s fetch noted by request id (`../retry-after.ts`), so the dialog can say how long to wait.
 * `createSipTrunk`'s `409 sip_trunk_limit_reached` (review C1) is raised as a
 * {@link SipTrunkMaximumReachedError}; its `maximum` is read from the envelope's `detail.maximum` when
 * the API sends one, and is `null` otherwise — the screen then states no number rather than a guess.
 */
import { ApiError, assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";
import {
  CALLER_ID_PRESENTATION,
  SIP_CHECK_OUTCOME_KIND,
  SIP_CHECK_TLS,
  SIP_CODEC_POLICY,
  SIP_INBOUND_DELIVERY,
  SIP_MEDIA_ENCRYPTION,
  SIP_REGISTRATION_STATE,
  SIP_TRANSPORT,
  SIP_TRUNK_STATE,
  WIRE_NUMBER_FORMAT,
  type CallerIdPresentation,
  type SipCheckOutcome,
  type SipCheckOutcomeKind,
  type SipCheckTls,
  type SipCodecPolicy,
  type SipInboundDelivery,
  type SipMediaEncryption,
  type SipRegistrationState,
  type SipTransport,
  type SipTrunkAnyInput,
  type SipTrunkAnySettingInput,
  type SipTrunkInboundNotReadyReason,
  type SipTrunkSettingInput,
  type SipTrunkState,
  type WireNumberFormat,
} from "@wetalk/domain";

import type { ConsoleGateway } from "../gateway.js";
import {
  MalformedResponseError,
  readArray,
  readBoolean,
  readRecord,
  readText,
  readTextArray,
  readWholeNumber,
} from "../decode.js";
import { refuseWithRetryAfter } from "../retry-after.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── Views ─────────────────────────────────────────────────────────────────────────────────

/** The codecs a trunk offers (`codec_offered`), in LiveKit's compiled order. */
export type SipOfferedCodec = "G722" | "PCMU" | "PCMA";
const SIP_OFFERED_CODEC: readonly SipOfferedCodec[] = ["G722", "PCMU", "PCMA"];

/** The codec a test conversation negotiated; `null` when it was not observed — never guessed. */
export type SipNegotiatedCodec = "G722" | "PCMU" | "PCMA" | "AMR-WB";
const SIP_NEGOTIATED_CODEC = () => (["G722", "PCMU", "PCMA", "AMR-WB"] as const);

const INBOUND_NOT_READY_REASON: readonly SipTrunkInboundNotReadyReason[] = [
  "inbound_off",
  "disabled",
  "not_verified",
  "no_allowed_address",
  "no_inbound_auth",
  "no_bound_number",
  "not_registered",
];

export type SipTrunkEventKind =
  | "invite_in"
  | "invite_out"
  | "final_response"
  | "answered"
  | "bye"
  | "check"
  | "auth_failure"
  | "unmatched_invite"
  | "refused"
  | "state_changed";

const SIP_TRUNK_EVENT_KIND: readonly SipTrunkEventKind[] = [
  "invite_in",
  "invite_out",
  "final_response",
  "answered",
  "bye",
  "check",
  "auth_failure",
  "unmatched_invite",
  "refused",
  "state_changed",
];

/** One number of a trunk, with the VoiceAgent its inbound conversations reach. */
export interface SipTrunkNumberView {
  readonly phoneNumberId: string;
  readonly e164: string;
  /** How the provider writes it, under the trunk's wire format. */
  readonly wireForm: string;
  readonly isCallerId: boolean;
  readonly voiceAgentId: string | null;
  readonly voiceAgentName: string | null;
  /** `false` while unbound or while the trunk is disabled. */
  readonly bindingEnabled: boolean;
}

/** The last inbound or outbound leg: when, and how it ended (`end_reason`, verbatim). */
export interface SipTrunkLegSummaryView {
  readonly atText: string;
  readonly endReason: string;
}

/**
 * What WeTalk's registration front last reported for a registration trunk (plan-008 ST-KM) — only what it
 * reported, never inferred: the state, when it was first seen in it, and the provider's SIP status for a
 * failure (`null` when none was reported).
 */
export interface SipTrunkRegistrationView {
  readonly state: SipRegistrationState;
  readonly observedAtText: string;
  readonly reasonCode: number | null;
}

export type SipTrunkInboundReadinessView =
  | { readonly ready: true }
  | { readonly ready: false; readonly reason: SipTrunkInboundNotReadyReason };

/**
 * A trunk, as the card and the edit dialog read it. **No password field exists here** — not a
 * masked one, not a length: the API never sends one, and a screen cannot echo what it never holds.
 */
export interface SipTrunkView {
  readonly sipTrunkId: string;
  readonly displayName: string;
  /** The provider's outbound server; EMPTY on an inbound-only trunk (`inboundOnly`) — never an address then. */
  readonly host: string;
  /** 0 on an inbound-only trunk (`inboundOnly`), which has no outbound port. */
  readonly port: number;
  readonly transport: SipTransport;
  readonly mediaEncryption: SipMediaEncryption;
  /** What WeTalk presents to the provider; EMPTY on an inbound-only trunk. */
  readonly authUsername: string;
  /**
   * plan-015 SIP (ADR 0046): the trunk has no outbound half — it only receives calls, is routable before it is
   * verified, and its first answered call verifies it. Absent (or `false`): a two-way trunk.
   */
  readonly inboundOnly?: boolean;
  /** WeTalk's generated inbound username, once the member turned inbound on; `null` before. */
  readonly inboundAuthUsername: string | null;
  readonly allowedAddress: readonly string[];
  readonly wireNumberFormat: WireNumberFormat;
  readonly nationalCountry: string | null;
  readonly callerIdPresentation: CallerIdPresentation;
  readonly allowedDestinationCountry: readonly string[];
  readonly codecPolicy: SipCodecPolicy;
  readonly codecOffered: readonly SipOfferedCodec[];
  /** How the provider sends calls: to WeTalk's address (`direct`) or to WeTalk's registration (ST-KM). */
  readonly inboundDelivery: SipInboundDelivery;
  /** `null` for a direct trunk, or a registration trunk the front has not reported on yet. */
  readonly registration: SipTrunkRegistrationView | null;
  readonly state: SipTrunkState;
  readonly inboundReadiness: SipTrunkInboundReadinessView;
  readonly verifiedAtText: string | null;
  readonly disabledAtText: string | null;
  /** The last connection check: when, and the outcome's kind (the answer carries no code or phrase). */
  readonly lastCheck: { readonly atText: string; readonly kind: SipCheckOutcomeKind } | null;
  readonly lastInbound: SipTrunkLegSummaryView | null;
  readonly lastOutbound: SipTrunkLegSummaryView | null;
  /** What the last leg actually used — observed, never the configured preference. */
  readonly lastLegEncryption: {
    readonly transport: SipTransport;
    readonly mediaEncryption: "none" | "srtp";
  } | null;
  readonly number: readonly SipTrunkNumberView[];
  readonly createdAtText: string;
  readonly updatedAtText: string;
}

/** What a member types into the provider, and what the platform allows (`readSipTrunkSettings`). */
export interface SipTrunkSettingsView {
  readonly sipEdgeHost: string;
  readonly sipEdgeAddress: string;
  readonly port: { readonly udp: number; readonly tcp: number; readonly tls: number };
  readonly rtpPortRange: { readonly start: number; readonly end: number };
  /** The reviewed countries a trunk may ring, each with the date its data was read. */
  readonly destinationCountry: readonly { readonly code: string; readonly reviewedAt: string | null }[];
  /** Countries whose national number form WeTalk can write. */
  readonly nationalCountry: readonly string[];
  readonly inbound: "on" | "off";
  readonly sipEdgeReachable: boolean;
}

/** `createSipTrunkInboundAuth`'s answer — the password exists here and in the dialog, nowhere else. */
export interface SipTrunkInboundAuthView {
  readonly inboundAuthUsername: string;
  readonly inboundPassword: string;
}

export interface SipTrunkCheckView {
  readonly outcome: SipCheckOutcome;
  readonly state: SipTrunkState;
  readonly checkedAtText: string;
}

/** `agent_calls_me`: the voice agent rings the member's own phone from a caller-ID number. */
export interface SipTrunkOutboundTestCallRequest {
  readonly direction: "agent_calls_me";
  readonly voiceAgentId: string;
  readonly callerIdPhoneNumberId: string;
  readonly partyNumber: string;
}

/** `i_call_the_agent`: arms a trunk number; the member's next conversation to it is the test. */
export interface SipTrunkInboundTestCallRequest {
  readonly direction: "i_call_the_agent";
  readonly phoneNumberId: string;
}

export type SipTrunkTestCallRequest = SipTrunkOutboundTestCallRequest | SipTrunkInboundTestCallRequest;

export type SipTrunkTestCallStartedView =
  | { readonly kind: "dialing"; readonly conversationId: string }
  | { readonly kind: "armed"; readonly phoneNumberId: string; readonly expectUntil: string };

export interface SipTrunkTestCallProgressView {
  readonly state: "idle" | "dialing" | "in_progress" | "ended";
  readonly conversationId: string | null;
  /** The voice agent that answered. */
  readonly voiceAgentId: string | null;
  readonly codec: SipNegotiatedCodec | null;
  readonly sampleRateHz: number | null;
  readonly endReason: string | null;
  readonly billedSecond: number | null;
}

/** One observed or synthesised SIP fact. The party number is masked by the API (`+30***1391`). */
export interface SipTrunkEventView {
  readonly sipTrunkEventId: string;
  readonly atText: string;
  readonly kind: SipTrunkEventKind;
  readonly direction: "inbound" | "outbound" | null;
  readonly responseCode: number | null;
  readonly partyMasked: string | null;
  /** Verbatim from the wire (`G722`, `PCMA`, …) or `null` when it was not observed. */
  readonly codec: string | null;
  readonly sampleRateHz: number | null;
  readonly transport: string | null;
  readonly mediaEncryption: string | null;
  readonly voiceAgentId: string | null;
  readonly conversationId: string | null;
  /** WeTalk derived it (a state change) rather than the SIP edge observing it. */
  readonly synthesised: boolean;
  /**
   * plan-015 SIP (ADR 0046): the state change was an inbound-only trunk's first answered call verifying it (the
   * API's `verified_by_first_call` kind; `kind` reads `state_changed`). Absent on every other event.
   */
  readonly verifiedByFirstCall?: true;
}

/** Whether a trunk only receives calls (plan-015 SIP): the one test the console uses. */
export function isInboundOnlyTrunkView(trunk: Pick<SipTrunkView, "inboundOnly">): boolean {
  return trunk.inboundOnly === true;
}

/** `updateSipTrunk`'s change: settings, and — only with the whole outbound half of an inbound-only trunk — its password. */
export type SipTrunkChange = Partial<SipTrunkSettingInput> & { readonly password?: string };

/**
 * `409 sip_trunk_limit_reached`: the account already holds its most SIP trunks. The API's `ApiError`,
 * plus the maximum when the envelope names it in `detail.maximum`.
 */
export class SipTrunkMaximumReachedError extends ApiError {
  /** The account's most trunks, or `null` when the answer did not say. */
  readonly maximum: number | null;
  constructor(result: ApiResult) {
    super(result.status, result.body, result.requestId);
    this.maximum = sipTrunkMaximumOfBody(result.body);
  }
}

/** `error.detail.maximum` as a whole number of at least 1, or `null` when the body carries none. */
export function sipTrunkMaximumOfBody(body: unknown): number | null {
  if (typeof body !== "object" || body === null) return null;
  const envelope = (body as { readonly error?: unknown }).error;
  if (typeof envelope !== "object" || envelope === null) return null;
  const detail = (envelope as { readonly detail?: unknown }).detail;
  if (typeof detail !== "object" || detail === null) return null;
  const maximum = (detail as { readonly maximum?: unknown }).maximum;
  return typeof maximum === "number" && Number.isInteger(maximum) && maximum >= 1 ? maximum : null;
}

/** Raises `createSipTrunk`'s trunk-maximum refusal as its own class; anything else passes through. */
function refuseOverMaximum(result: ApiResult): void {
  if (result.status !== 409) return;
  const envelope = (result.body as { readonly error?: { readonly code?: unknown } } | null)?.error;
  if (envelope?.code === "sip_trunk_limit_reached") throw new SipTrunkMaximumReachedError(result);
}

/** A 2xx answer the console could not decode, with the request id to quote. */
export class SipTrunkReadError extends Error {
  override readonly name = "SipTrunkReadError";
  readonly requestId: string | null;
  constructor(cause: Error, requestId: string | null) {
    super(portalText("shell", "wetalk_answered_in_a_way_this_page_cannot_read_value_65d8fa72", { v0: String(cause.message) }));
    this.requestId = requestId;
  }
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The workspace's trunks, oldest first, each with its numbers. Any member reads them. */
    listSipTrunks(): Promise<readonly SipTrunkView[]>;
    getSipTrunk(sipTrunkId: string): Promise<SipTrunkView>;
    /** WeTalk's SIP host and address, the ports, and the reviewed countries — rendered, never literal. */
    readSipTrunkSettings(): Promise<SipTrunkSettingsView>;
    /**
     * The password goes to the vault in this request and is never readable again. An inbound-only draft (no
     * outbound half, plan-015 SIP) sends none; WeTalk makes its sign-in details, shown by createSipTrunkInboundAuth.
     */
    createSipTrunk(draft: SipTrunkAnyInput): Promise<SipTrunkView>;
    /**
     * Any setting except the numbers; a connection field makes it unverified. The password only with an inbound-only
     * trunk's whole outbound half (then the API checks the connection straight after).
     */
    updateSipTrunk(sipTrunkId: string, change: SipTrunkChange): Promise<SipTrunkView>;
    /** Write-only. Resolves when the vault has the new password. */
    replaceSipTrunkPassword(sipTrunkId: string, password: string): Promise<void>;
    /** 409 `sip_trunk_in_use` while a running campaign or a conversation in progress uses it. */
    deleteSipTrunk(sipTrunkId: string): Promise<void>;
    disableSipTrunk(sipTrunkId: string): Promise<SipTrunkView>;
    enableSipTrunk(sipTrunkId: string): Promise<SipTrunkView>;
    /** Generates both halves; the password is in this answer only. Calling again replaces both. */
    createSipTrunkInboundAuth(sipTrunkId: string): Promise<SipTrunkInboundAuthView>;
    /** Inbound turns off for the trunk. */
    deleteSipTrunkInboundAuth(sipTrunkId: string): Promise<void>;
    addSipTrunkNumber(
      sipTrunkId: string,
      number: { readonly e164: string; readonly isCallerId: boolean },
    ): Promise<SipTrunkNumberView>;
    updateSipTrunkNumber(sipTrunkId: string, phoneNumberId: string, isCallerId: boolean): Promise<SipTrunkNumberView>;
    removeSipTrunkNumber(sipTrunkId: string, phoneNumberId: string): Promise<void>;
    bindSipTrunkNumber(sipTrunkId: string, phoneNumberId: string, voiceAgentId: string): Promise<SipTrunkNumberView>;
    unbindSipTrunkNumber(sipTrunkId: string, phoneNumberId: string): Promise<void>;
    /** Free, never rings anyone; 429 `sip_trunk_check_cooldown` states the wait in its message. */
    checkSipTrunk(sipTrunkId: string): Promise<SipTrunkCheckView>;
    createSipTrunkTestCall(sipTrunkId: string, request: SipTrunkTestCallRequest): Promise<SipTrunkTestCallStartedView>;
    /** The newest test conversation on the number that started at or after `since` (ISO 8601). */
    readSipTrunkTestCall(sipTrunkId: string, phoneNumberId: string, since: string): Promise<SipTrunkTestCallProgressView>;
    endSipTrunkTestCall(sipTrunkId: string, conversationId: string): Promise<void>;
    /** The latest SIP facts of the trunk, newest first (50 unless asked otherwise). */
    listSipTrunkEvents(sipTrunkId: string): Promise<readonly SipTrunkEventView[]>;
  }
}

export type SipTrunkGatewaySlice = Pick<
  ConsoleGateway,
  | "listSipTrunks"
  | "getSipTrunk"
  | "readSipTrunkSettings"
  | "createSipTrunk"
  | "updateSipTrunk"
  | "replaceSipTrunkPassword"
  | "deleteSipTrunk"
  | "disableSipTrunk"
  | "enableSipTrunk"
  | "createSipTrunkInboundAuth"
  | "deleteSipTrunkInboundAuth"
  | "addSipTrunkNumber"
  | "updateSipTrunkNumber"
  | "removeSipTrunkNumber"
  | "bindSipTrunkNumber"
  | "unbindSipTrunkNumber"
  | "checkSipTrunk"
  | "createSipTrunkTestCall"
  | "readSipTrunkTestCall"
  | "endSipTrunkTestCall"
  | "listSipTrunkEvents"
>;

/** Empty: ST-AT documented every operation this slice calls. */
export const sipTrunkGap: readonly GatewayGap[] = [];

/** The page size the activity card reads (design §22.14: the latest 50). */
export const SIP_TRUNK_EVENT_PAGE = 50;

// ── Bodies (shared with the sandbox, so both send and validate the same wire shape) ──────────

/**
 * `SipTrunkCreate` in snake case, exactly what `createSipTrunk` sends. An inbound-only draft (plan-015 SIP) names
 * none of the outbound half — that absence is what makes it inbound-only.
 */
export function sipTrunkCreateBodyOf(draft: SipTrunkAnyInput): Record<string, unknown> {
  const { password: _password, number: _number, ...setting } = draft;
  return {
    ...sipTrunkSettingBodyOf(setting),
    ...(draft.password === null ? {} : { password: draft.password }),
    number: draft.number.map((one) => ({ e164: one.e164, is_caller_id: one.isCallerId })),
  };
}

/**
 * The settings half of a body; a field left `undefined` is left out (a PATCH names only changes), and so is an
 * absent outbound field (`null`, an inbound-only trunk's). A change's `password` goes with the outbound half.
 */
export function sipTrunkSettingBodyOf(change: Partial<SipTrunkAnySettingInput> & { readonly password?: string }): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const put = (name: string, value: unknown): void => {
    if (value !== undefined && value !== null) body[name] = value;
  };
  put("display_name", change.displayName);
  put("host", change.host);
  put("port", change.port);
  put("transport", change.transport);
  put("media_encryption", change.mediaEncryption);
  put("auth_username", change.authUsername);
  put("password", change.password);
  put("allowed_address", change.allowedAddress === undefined ? undefined : [...change.allowedAddress]);
  put("wire_number_format", change.wireNumberFormat);
  put("national_country", change.nationalCountry);
  put("caller_id_presentation", change.callerIdPresentation);
  put(
    "allowed_destination_country",
    change.allowedDestinationCountry === undefined || change.allowedDestinationCountry === null
      ? undefined
      : [...change.allowedDestinationCountry],
  );
  put("codec_policy", change.codecPolicy);
  put("inbound_delivery", change.inboundDelivery);
  return body;
}

// ── Decoders ──────────────────────────────────────────────────────────────────────────────

function oneOf<T extends string>(value: unknown, path: string, allowed: readonly T[]): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, allowed.join(" | "), raw);
  return found;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

function nullableNumber(value: unknown, path: string): number | null {
  return value === null ? null : readWholeNumber(value, path);
}

/** An optional field of a progress answer: absent and `null` both read as "not there". */
function optionalText(value: unknown, path: string): string | null {
  return value === undefined || value === null ? null : readText(value, path);
}

function optionalNumber(value: unknown, path: string): number | null {
  return value === undefined || value === null ? null : readWholeNumber(value, path);
}

type When = (isoTimestamp: string) => string;

function nullableWhen(value: unknown, path: string, when: When): string | null {
  return value === null ? null : when(readText(value, path));
}

export function sipTrunkNumberOfWire(value: unknown, path: string): SipTrunkNumberView {
  const one = readRecord(value, path);
  return {
    phoneNumberId: readText(one["phone_number_id"], `${path}.phone_number_id`),
    e164: readText(one["e164"], `${path}.e164`),
    wireForm: readText(one["wire_form"], `${path}.wire_form`),
    isCallerId: readBoolean(one["is_caller_id"], `${path}.is_caller_id`),
    voiceAgentId: nullableText(one["voice_agent_id"], `${path}.voice_agent_id`),
    voiceAgentName: nullableText(one["voice_agent_name"], `${path}.voice_agent_name`),
    bindingEnabled: readBoolean(one["binding_enabled"], `${path}.binding_enabled`),
  };
}

function legSummaryOf(value: unknown, path: string, when: When): SipTrunkLegSummaryView | null {
  if (value === null) return null;
  const one = readRecord(value, path);
  return validatedDisplayView({
    get atText() { return when(readText(one["at"], `${path}.at`)); },
    endReason: readText(one["end_reason"], `${path}.end_reason`),
  });
}

function registrationOf(value: unknown, path: string, when: When): SipTrunkRegistrationView | null {
  if (value === null) return null;
  const one = readRecord(value, path);
  return validatedDisplayView({
    state: oneOf(one["state"], `${path}.state`, SIP_REGISTRATION_STATE),
    get observedAtText() { return when(readText(one["observed_at"], `${path}.observed_at`)); },
    reasonCode: nullableNumber(one["reason_code"], `${path}.reason_code`),
  });
}

function readinessOf(value: unknown, path: string): SipTrunkInboundReadinessView {
  const one = readRecord(value, path);
  if (readBoolean(one["ready"], `${path}.ready`)) return { ready: true };
  return { ready: false, reason: oneOf(one["reason"], `${path}.reason`, INBOUND_NOT_READY_REASON) };
}

export function sipTrunkOfWire(value: unknown, path: string, when: When): SipTrunkView {
  const one = readRecord(value, path);
  // plan-015 SIP: an inbound-only trunk's outbound half is `null`, all of it, and it has no password; a two-way
  // trunk has every part and its password. Anything between is a malformed answer, not a state to render.
  const inboundOnly = one["host"] === null;
  const passwordSet = readBoolean(one["password_set"], `${path}.password_set`);
  if (passwordSet === inboundOnly) {
    throw new MalformedResponseError(`${path}.password_set`, inboundOnly ? "false" : "true", passwordSet);
  }
  if (inboundOnly) {
    for (const field of ["port", "auth_username", "allowed_destination_country"]) {
      if (one[field] !== null) throw new MalformedResponseError(`${path}.${field}`, "null", one[field]);
    }
  }
  const lastCheck = one["last_check"];
  const encryption = one["last_leg_encryption"];
  return validatedDisplayView({
    sipTrunkId: readText(one["sip_trunk_id"], `${path}.sip_trunk_id`),
    displayName: readText(one["display_name"], `${path}.display_name`),
    host: inboundOnly ? "" : readText(one["host"], `${path}.host`),
    port: inboundOnly ? 0 : readWholeNumber(one["port"], `${path}.port`),
    transport: oneOf(one["transport"], `${path}.transport`, SIP_TRANSPORT),
    mediaEncryption: oneOf(one["media_encryption"], `${path}.media_encryption`, SIP_MEDIA_ENCRYPTION),
    authUsername: inboundOnly ? "" : readText(one["auth_username"], `${path}.auth_username`),
    ...(inboundOnly ? { inboundOnly: true } : {}),
    inboundAuthUsername: nullableText(one["inbound_auth_username"], `${path}.inbound_auth_username`),
    allowedAddress: readTextArray(one["allowed_address"], `${path}.allowed_address`),
    wireNumberFormat: oneOf(one["wire_number_format"], `${path}.wire_number_format`, WIRE_NUMBER_FORMAT),
    nationalCountry: nullableText(one["national_country"], `${path}.national_country`),
    callerIdPresentation: oneOf(one["caller_id_presentation"], `${path}.caller_id_presentation`, CALLER_ID_PRESENTATION),
    allowedDestinationCountry: inboundOnly
      ? []
      : readTextArray(one["allowed_destination_country"], `${path}.allowed_destination_country`),
    codecPolicy: oneOf(one["codec_policy"], `${path}.codec_policy`, SIP_CODEC_POLICY),
    codecOffered: readArray(one["codec_offered"], `${path}.codec_offered`).map((entry, index) =>
      oneOf(entry, `${path}.codec_offered[${index}]`, SIP_OFFERED_CODEC),
    ),
    inboundDelivery: oneOf(one["inbound_delivery"], `${path}.inbound_delivery`, SIP_INBOUND_DELIVERY),
    registration: registrationOf(one["registration"], `${path}.registration`, when),
    state: oneOf(one["state"], `${path}.state`, SIP_TRUNK_STATE),
    inboundReadiness: readinessOf(one["inbound_readiness"], `${path}.inbound_readiness`),
    get verifiedAtText() { return nullableWhen(one["verified_at"], `${path}.verified_at`, when); },
    get disabledAtText() { return nullableWhen(one["disabled_at"], `${path}.disabled_at`, when); },
    lastCheck:
      lastCheck === null
        ? null
        : validatedDisplayView({
            get atText() { return when(readText(readRecord(lastCheck, `${path}.last_check`)["at"], `${path}.last_check.at`)); },
            kind: oneOf(
              readRecord(lastCheck, `${path}.last_check`)["outcome"],
              `${path}.last_check.outcome`,
              SIP_CHECK_OUTCOME_KIND,
            ),
          }),
    lastInbound: legSummaryOf(one["last_inbound"], `${path}.last_inbound`, when),
    lastOutbound: legSummaryOf(one["last_outbound"], `${path}.last_outbound`, when),
    lastLegEncryption:
      encryption === null
        ? null
        : {
            transport: oneOf(
              readRecord(encryption, `${path}.last_leg_encryption`)["transport"],
              `${path}.last_leg_encryption.transport`,
              SIP_TRANSPORT,
            ),
            mediaEncryption: oneOf(
              readRecord(encryption, `${path}.last_leg_encryption`)["media_encryption"],
              `${path}.last_leg_encryption.media_encryption`,
              ["none", "srtp"] as const,
            ),
          },
    number: readArray(one["number"], `${path}.number`).map((entry, index) =>
      sipTrunkNumberOfWire(entry, `${path}.number[${index}]`),
    ),
    get createdAtText() { return when(readText(one["created_at"], `${path}.created_at`)); },
    get updatedAtText() { return when(readText(one["updated_at"], `${path}.updated_at`)); },
  });
}

export function sipTrunkSettingsOfWire(value: unknown, path: string): SipTrunkSettingsView {
  const one = readRecord(value, path);
  const port = readRecord(one["port"], `${path}.port`);
  const range = readRecord(one["rtp_port_range"], `${path}.rtp_port_range`);
  return {
    sipEdgeHost: readText(one["sip_edge_host"], `${path}.sip_edge_host`),
    sipEdgeAddress: readText(one["sip_edge_address"], `${path}.sip_edge_address`),
    port: {
      udp: readWholeNumber(port["udp"], `${path}.port.udp`),
      tcp: readWholeNumber(port["tcp"], `${path}.port.tcp`),
      tls: readWholeNumber(port["tls"], `${path}.port.tls`),
    },
    rtpPortRange: {
      start: readWholeNumber(range["start"], `${path}.rtp_port_range.start`),
      end: readWholeNumber(range["end"], `${path}.rtp_port_range.end`),
    },
    destinationCountry: readArray(one["destination_country"], `${path}.destination_country`).map((entry, index) => {
      const at = `${path}.destination_country[${index}]`;
      const country = readRecord(entry, at);
      return {
        code: readText(country["code"], `${at}.code`),
        reviewedAt: nullableText(country["reviewed_at"], `${at}.reviewed_at`),
      };
    }),
    nationalCountry: readTextArray(one["national_country"], `${path}.national_country`),
    inbound: oneOf(one["inbound"], `${path}.inbound`, ["on", "off"] as const),
    sipEdgeReachable: readBoolean(one["sip_edge_reachable"], `${path}.sip_edge_reachable`),
  };
}

/** `SipCheckOutcome` (snake case) → the domain's union (IC-75), every arm read strictly. */
export function sipCheckOutcomeOfWire(value: unknown, path: string): SipCheckOutcome {
  const one = readRecord(value, path);
  const kind = oneOf(one["kind"], `${path}.kind`, SIP_CHECK_OUTCOME_KIND);
  const tls = (): SipCheckTls => oneOf(one["tls"], `${path}.tls`, SIP_CHECK_TLS);
  switch (kind) {
    case "dns_failed":
    case "blocked_destination":
    case "unreachable":
    case "tls_failed":
      return { kind };
    case "not_verified":
    case "sip_reachable_unauthenticated":
    case "credentials_accepted":
      return { kind, tls: tls() };
    case "credentials_rejected":
      return { kind, statusCode: readWholeNumber(one["status_code"], `${path}.status_code`), tls: tls() };
    case "sip_error":
      return {
        kind,
        statusCode: readWholeNumber(one["status_code"], `${path}.status_code`),
        reasonPhrase: readText(one["reason_phrase"], `${path}.reason_phrase`),
        tls: tls(),
      };
  }
}

export function sipTrunkEventOfWire(value: unknown, path: string, when: When): SipTrunkEventView {
  const one = readRecord(value, path);
  const direction = one["direction"];
  // plan-015 SIP (ADR 0046): the first-call verification is the state change it is (see the header).
  const verifiedByFirstCall = one["kind"] === "verified_by_first_call";
  return validatedDisplayView({
    sipTrunkEventId: readText(one["sip_trunk_event_id"], `${path}.sip_trunk_event_id`),
    get atText() { return when(readText(one["occurred_at"], `${path}.occurred_at`)); },
    kind: verifiedByFirstCall ? "state_changed" : oneOf(one["kind"], `${path}.kind`, SIP_TRUNK_EVENT_KIND),
    ...(verifiedByFirstCall ? { verifiedByFirstCall: true as const } : {}),
    direction: direction === null ? null : oneOf(direction, `${path}.direction`, ["inbound", "outbound"] as const),
    responseCode: nullableNumber(one["response_code"], `${path}.response_code`),
    partyMasked: nullableText(one["party_masked"], `${path}.party_masked`),
    codec: nullableText(one["codec"], `${path}.codec`),
    sampleRateHz: nullableNumber(one["sample_rate_hz"], `${path}.sample_rate_hz`),
    transport: nullableText(one["transport"], `${path}.transport`),
    mediaEncryption: nullableText(one["media_encryption"], `${path}.media_encryption`),
    voiceAgentId: nullableText(one["voice_agent_id"], `${path}.voice_agent_id`),
    conversationId: nullableText(one["conversation_id"], `${path}.conversation_id`),
    synthesised: readBoolean(one["synthesised"], `${path}.synthesised`),
  });
}

export function sipTrunkTestCallStartedOfWire(value: unknown, path: string): SipTrunkTestCallStartedView {
  const one = readRecord(value, path);
  if (one["conversation_id"] !== undefined) {
    oneOf(one["state"], `${path}.state`, ["dialing"] as const);
    return { kind: "dialing", conversationId: readText(one["conversation_id"], `${path}.conversation_id`) };
  }
  return {
    kind: "armed",
    phoneNumberId: readText(one["phone_number_id"], `${path}.phone_number_id`),
    expectUntil: readText(one["expect_until"], `${path}.expect_until`),
  };
}

export function sipTrunkTestCallProgressOfWire(value: unknown, path: string): SipTrunkTestCallProgressView {
  const one = readRecord(value, path);
  const codec = one["codec"];
  return {
    state: oneOf(one["state"], `${path}.state`, ["idle", "dialing", "in_progress", "ended"] as const),
    conversationId: optionalText(one["conversation_id"], `${path}.conversation_id`),
    voiceAgentId: optionalText(one["voice_agent_id"], `${path}.voice_agent_id`),
    codec: codec === undefined || codec === null ? null : oneOf(codec, `${path}.codec`, SIP_NEGOTIATED_CODEC()),
    sampleRateHz: optionalNumber(one["sample_rate_hz"], `${path}.sample_rate_hz`),
    endReason: optionalText(one["end_reason"], `${path}.end_reason`),
    billedSecond: optionalNumber(one["billed_second"], `${path}.billed_second`),
  };
}

function decodedOf<T>(result: ApiResult, decode: (body: unknown) => T): T {
  const body = assertOk(result);
  try {
    return decode(body);
  } catch (error) {
    if (error instanceof MalformedResponseError) throw new SipTrunkReadError(error, result.requestId);
    throw error;
  }
}

function dataOf(body: unknown, operationId: string): unknown {
  return readRecord(body, operationId)["data"];
}

function idempotencyHeader(): Readonly<Record<string, string>> {
  return { "idempotency-key": crypto.randomUUID() };
}

export function sipTrunkApiSlice(client: WeTalkApiClient, context: SliceContext): SipTrunkGatewaySlice {
  const when: When = (isoTimestamp) => context.formatWhen(isoTimestamp, context.now());
  const trunkOf = (result: ApiResult, operationId: string): SipTrunkView =>
    decodedOf(result, (body) => sipTrunkOfWire(dataOf(body, operationId), `${operationId}.data`, when));
  const numberOf = (result: ApiResult, operationId: string): SipTrunkNumberView =>
    decodedOf(result, (body) => sipTrunkNumberOfWire(dataOf(body, operationId), `${operationId}.data`));
  const path = (sipTrunkId: string): { readonly sip_trunk_id: string } => ({ sip_trunk_id: sipTrunkId });

  return {
    async listSipTrunks(): Promise<readonly SipTrunkView[]> {
      const result = await client.listSipTrunks();
      return decodedOf(result, (body) =>
        readArray(dataOf(body, "listSipTrunks"), "listSipTrunks.data").map((entry, index) =>
          sipTrunkOfWire(entry, `listSipTrunks.data[${String(index)}]`, when),
        ),
      );
    },

    async getSipTrunk(sipTrunkId: string): Promise<SipTrunkView> {
      return trunkOf(await client.getSipTrunk({ path: path(sipTrunkId) }), "getSipTrunk");
    },

    async readSipTrunkSettings(): Promise<SipTrunkSettingsView> {
      const result = await client.readSipTrunkSettings();
      return decodedOf(result, (body) => sipTrunkSettingsOfWire(dataOf(body, "readSipTrunkSettings"), "readSipTrunkSettings.data"));
    },

    async createSipTrunk(draft: SipTrunkAnyInput): Promise<SipTrunkView> {
      const result = await client.createSipTrunk({ header: idempotencyHeader(), body: sipTrunkCreateBodyOf(draft) });
      refuseOverMaximum(result);
      return trunkOf(result, "createSipTrunk");
    },

    async updateSipTrunk(sipTrunkId: string, change: SipTrunkChange): Promise<SipTrunkView> {
      const result = await client.updateSipTrunk({ path: path(sipTrunkId), body: sipTrunkSettingBodyOf(change) });
      return trunkOf(result, "updateSipTrunk");
    },

    async replaceSipTrunkPassword(sipTrunkId: string, password: string): Promise<void> {
      assertOk(await client.replaceSipTrunkPassword({ path: path(sipTrunkId), body: { password } }));
    },

    async deleteSipTrunk(sipTrunkId: string): Promise<void> {
      assertOk(await client.deleteSipTrunk({ path: path(sipTrunkId) }));
    },

    async disableSipTrunk(sipTrunkId: string): Promise<SipTrunkView> {
      return trunkOf(await client.disableSipTrunk({ path: path(sipTrunkId), body: {} }), "disableSipTrunk");
    },

    async enableSipTrunk(sipTrunkId: string): Promise<SipTrunkView> {
      return trunkOf(await client.enableSipTrunk({ path: path(sipTrunkId), body: {} }), "enableSipTrunk");
    },

    async createSipTrunkInboundAuth(sipTrunkId: string): Promise<SipTrunkInboundAuthView> {
      const result = await client.createSipTrunkInboundAuth({ path: path(sipTrunkId), body: {} });
      return decodedOf(result, (body) => {
        const data = readRecord(dataOf(body, "createSipTrunkInboundAuth"), "createSipTrunkInboundAuth.data");
        return {
          inboundAuthUsername: readText(data["inbound_auth_username"], "createSipTrunkInboundAuth.data.inbound_auth_username"),
          inboundPassword: readText(data["inbound_password"], "createSipTrunkInboundAuth.data.inbound_password"),
        };
      });
    },

    async deleteSipTrunkInboundAuth(sipTrunkId: string): Promise<void> {
      assertOk(await client.deleteSipTrunkInboundAuth({ path: path(sipTrunkId) }));
    },

    async addSipTrunkNumber(sipTrunkId, number): Promise<SipTrunkNumberView> {
      const result = await client.addSipTrunkNumber({
        path: path(sipTrunkId),
        header: idempotencyHeader(),
        body: { e164: number.e164, is_caller_id: number.isCallerId },
      });
      return numberOf(result, "addSipTrunkNumber");
    },

    async updateSipTrunkNumber(sipTrunkId, phoneNumberId, isCallerId): Promise<SipTrunkNumberView> {
      const result = await client.updateSipTrunkNumber({
        path: { sip_trunk_id: sipTrunkId, phone_number_id: phoneNumberId },
        body: { is_caller_id: isCallerId },
      });
      return numberOf(result, "updateSipTrunkNumber");
    },

    async removeSipTrunkNumber(sipTrunkId, phoneNumberId): Promise<void> {
      assertOk(await client.removeSipTrunkNumber({ path: { sip_trunk_id: sipTrunkId, phone_number_id: phoneNumberId } }));
    },

    async bindSipTrunkNumber(sipTrunkId, phoneNumberId, voiceAgentId): Promise<SipTrunkNumberView> {
      const result = await client.bindSipTrunkNumber({
        path: { sip_trunk_id: sipTrunkId, phone_number_id: phoneNumberId },
        body: { voice_agent_id: voiceAgentId },
      });
      return numberOf(result, "bindSipTrunkNumber");
    },

    async unbindSipTrunkNumber(sipTrunkId, phoneNumberId): Promise<void> {
      assertOk(await client.unbindSipTrunkNumber({ path: { sip_trunk_id: sipTrunkId, phone_number_id: phoneNumberId } }));
    },

    async checkSipTrunk(sipTrunkId: string): Promise<SipTrunkCheckView> {
      const result = await client.checkSipTrunk({ path: path(sipTrunkId), body: {} });
      return decodedOf(result, (body) => {
        const data = readRecord(dataOf(body, "checkSipTrunk"), "checkSipTrunk.data");
        return validatedDisplayView({
          outcome: sipCheckOutcomeOfWire(data["outcome"], "checkSipTrunk.data.outcome"),
          state: oneOf(data["state"], "checkSipTrunk.data.state", SIP_TRUNK_STATE),
          get checkedAtText() { return when(readText(data["checked_at"], "checkSipTrunk.data.checked_at")); },
        });
      });
    },

    async createSipTrunkTestCall(sipTrunkId, request): Promise<SipTrunkTestCallStartedView> {
      const body =
        request.direction === "agent_calls_me"
          ? {
              direction: request.direction,
              voice_agent_id: request.voiceAgentId,
              caller_id_phone_number_id: request.callerIdPhoneNumberId,
              party_number: request.partyNumber,
            }
          : { direction: request.direction, phone_number_id: request.phoneNumberId };
      const result = await client.createSipTrunkTestCall({ path: path(sipTrunkId), header: idempotencyHeader(), body });
      refuseWithRetryAfter(result, context.takeRetryAfterSecond);
      return decodedOf(result, (answer) =>
        sipTrunkTestCallStartedOfWire(dataOf(answer, "createSipTrunkTestCall"), "createSipTrunkTestCall.data"),
      );
    },

    async readSipTrunkTestCall(sipTrunkId, phoneNumberId, since): Promise<SipTrunkTestCallProgressView> {
      const result = await client.readSipTrunkTestCall({
        path: path(sipTrunkId),
        query: { phone_number_id: phoneNumberId, since },
      });
      return decodedOf(result, (body) =>
        sipTrunkTestCallProgressOfWire(dataOf(body, "readSipTrunkTestCall"), "readSipTrunkTestCall.data"),
      );
    },

    async endSipTrunkTestCall(sipTrunkId, conversationId): Promise<void> {
      assertOk(
        await client.endSipTrunkTestCall({ path: { sip_trunk_id: sipTrunkId, conversation_id: conversationId }, body: {} }),
      );
    },

    async listSipTrunkEvents(sipTrunkId: string): Promise<readonly SipTrunkEventView[]> {
      const result = await client.listSipTrunkEvents({ path: path(sipTrunkId), query: { limit: SIP_TRUNK_EVENT_PAGE } });
      return decodedOf(result, (body) =>
        readArray(dataOf(body, "listSipTrunkEvents"), "listSipTrunkEvents.data").map((entry, index) =>
          sipTrunkEventOfWire(entry, `listSipTrunkEvents.data[${String(index)}]`, when),
        ),
      );
    },
  };
}
